import {
  useDataContext,
  useFeatureExtractionContext,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import {
  SelectionTree,
  SelectionTreeProps,
  SortableKeyOf,
  SortDirection,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import { createMemo, JSX, Show } from 'solid-js';
import { FxBreaklineTypeRowActions } from './FxBreaklineTypeRowActions';
import { FxBreaklinesPublishAllButton } from './FxBreaklinesPublishAllButton';
import { isExtractedFeatureTreeNodeKind } from './fx-workspace-tree-nodes';
import {
  FxWorkspaceTreeItem,
  isFxMapLayerReference,
  isProjectDataFxReference,
} from './fx-workspace-tree.types';
import {
  cancelProjectDataLoadItem,
  isProjectDataLoadItemLoading,
  isProjectDataLoadItemVisible,
  toggleProjectDataLoadItem,
} from './project-data-load-control';

export interface FxWorkspacesSelectionTreeContainerProps {
  nodes: TreeNodeDefinition<FxWorkspaceTreeItem>[];
  childrenLoadStatus?: SelectionTreeProps<FxWorkspaceTreeItem>['childrenLoadStatus'];
  defaultExpandIds?: string[];
  filterKey?: string;
  filterText?: string;
  highlightText?: string;
  sortBy: SortableKeyOf<TreeNodeDefinition<FxWorkspaceTreeItem>>;
  sortDirection: SortDirection;
}

export const FxWorkspacesSelectionTreeContainer = (
  props: FxWorkspacesSelectionTreeContainerProps
): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const { dataState, dataLoadingService } = useDataContext();
  const featureExtraction = useFeatureExtractionContext();

  const nodes = createMemo(() =>
    props.nodes.map((node) => {
      if (node.data?.kind === 'breaklines') {
        const { workspaceId } = node.data;

        return {
          ...node,
          visibleContent: () => (
            <div class="flex items-center pr-2">
              <FxBreaklinesPublishAllButton workspaceId={workspaceId} />
            </div>
          ),
        };
      }

      if (
        node.data?.kind === 'breaklineType' &&
        node.data.breaklineType !== undefined
      ) {
        const { workspaceId, breaklineType } = node.data;

        return {
          ...node,
          visibleContent: () => (
            <FxBreaklineTypeRowActions
              workspaceId={workspaceId}
              breaklineType={breaklineType}
            />
          ),
        };
      }

      return node;
    })
  );

  const isNodeSelected = (nodeId: string): boolean => {
    const node = props.nodes.find((item) => item.id === nodeId);
    const data = node?.data;
    if (isFxMapLayerReference(data)) {
      return featureExtraction.editingState.selectedLayerId === data.layerId;
    }
    if (data?.kind === 'job' && data.jobId !== undefined) {
      return (
        fxWorkspace.selectedJobId() === data.jobId &&
        fxWorkspace.selectedWorkspaceId() === data.workspaceId
      );
    }
    return false;
  };

  const handleNodeExpand = (nodeId: string): void => {
    const node = props.nodes.find((item) => item.id === nodeId);
    if (
      node?.data?.kind === 'breaklines' ||
      node?.data?.kind === 'extractedFeatures'
    ) {
      void fxWorkspace.loadWorkspaceBreaklines(node.data.workspaceId);
    }
  };

  const handleNodeSelect = (
    _nodeId: string,
    node: TreeNodeDefinition<FxWorkspaceTreeItem>
  ): void => {
    if (isFxMapLayerReference(node.data)) {
      featureExtraction.setSelectedLayerId(node.data.layerId);
      return;
    }

    if (node.data?.kind === 'job' && node.data.jobId !== undefined) {
      fxWorkspace.setSelectedWorkspaceId(node.data.workspaceId);
      fxWorkspace.setSelectedJobId(node.data.jobId);
    }
  };

  const handleVisibilityToggle = (nodeId: string): void => {
    const node = props.nodes.find((item) => item.id === nodeId);
    if (isProjectDataFxReference(node?.data)) {
      toggleProjectDataLoadItem(
        dataLoadingService,
        dataState,
        node.data.loadItemType,
        node.data.refTrn
      );
      return;
    }

    if (isFxMapLayerReference(node?.data)) {
      if (featureExtraction.layersLoadStatus() !== 'success') {
        return;
      }
      featureExtraction.toggleLayerVisibility(node.data.layerId);
      return;
    }

    if (isExtractedFeatureTreeNodeKind(node?.data?.kind)) {
      fxWorkspace.toggleExtractedFeatureNodeVisibility(nodeId);
    }
  };

  const handleCancelVisibilityToggle = (nodeId: string): void => {
    const node = props.nodes.find((item) => item.id === nodeId);
    if (isProjectDataFxReference(node?.data)) {
      cancelProjectDataLoadItem(
        dataLoadingService,
        dataState,
        node.data.loadItemType,
        node.data.refTrn
      );
    }
  };

  const isNodeVisible = (nodeId: string): boolean => {
    const node = props.nodes.find((item) => item.id === nodeId);
    if (isProjectDataFxReference(node?.data)) {
      return isProjectDataLoadItemVisible(
        dataState,
        node.data.loadItemType,
        node.data.refTrn
      );
    }

    if (isFxMapLayerReference(node?.data)) {
      return featureExtraction.isLayerVisible(node.data.layerId);
    }

    if (isExtractedFeatureTreeNodeKind(node?.data?.kind)) {
      return fxWorkspace.isExtractedFeatureNodeVisible(nodeId);
    }

    return true;
  };

  const isNodeLoading = (nodeId: string): boolean => {
    const node = props.nodes.find((item) => item.id === nodeId);
    if (isProjectDataFxReference(node?.data)) {
      return isProjectDataLoadItemLoading(
        dataState,
        node.data.loadItemType,
        node.data.refTrn
      );
    }

    return false;
  };

  const isNodeRetrieved = (nodeId: string): boolean => {
    const node = props.nodes.find((item) => item.id === nodeId);
    return (
      node?.data?.kind === 'reference' ||
      isExtractedFeatureTreeNodeKind(node?.data?.kind)
    );
  };

  const canRenderTree = createMemo(
    () => props.childrenLoadStatus === 'error' || props.nodes.length > 0
  );

  return (
    <div data-testid="fx-workspaces-selection-tree">
      <Show when={canRenderTree()} keyed>
        {(_ready) => (
          <Show when={props.filterKey ?? 'default'} keyed>
            {(_filterKey) => (
              <SelectionTree
                multiSelect={false}
                childrenLoadStatus={props.childrenLoadStatus ?? 'pending'}
                nodes={nodes()}
                sortBy={props.sortBy}
                sortDirection={props.sortDirection}
                filterText={props.filterText}
                highlightText={props.highlightText}
                filterMode="all"
                defaultExpandIds={props.defaultExpandIds}
                isNodeLoading={isNodeLoading}
                isNodeVisible={isNodeVisible}
                isNodeRetrieved={isNodeRetrieved}
                onVisibilityToggle={handleVisibilityToggle}
                onCancelVisibilityToggle={handleCancelVisibilityToggle}
                onNodeExpand={handleNodeExpand}
                isNodeSelected={isNodeSelected}
                onNodeSelect={handleNodeSelect}
              />
            )}
          </Show>
        )}
      </Show>
    </div>
  );
};
